import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {ClockLine, CommentLine} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {activeCommentAtom, focusedThreadAtom} from 'atoms/comment';
import {loggedInEmployeeAtom} from 'atoms/employee';
import cx from 'classnames';
import {EditorDocStatus, EditorDocTemplateType, EmployeeRoleLevel} from 'client';
import {
  EditorFocusManagerContext,
  useEditorFocusManagerProvider,
} from 'components/DSYS/CollabProvider';
import {ExcludeWordcountNode} from 'components/DSYS/editor/nodes/ExcludeWordcountNode';
import {PlaceholderNode} from 'components/DSYS/editor/nodes/PlaceholderNode';
import {COMMENT_DISPLAY_WIDTH_PX} from 'components/DSYS/editor/plugins/commentDisplaySizing';
import {DocumentThreadsProvider} from 'components/DSYS/editor/plugins/comments/ThreadsProvider';
import {useThreadsWithoutContext} from 'components/DSYS/editor/plugins/comments/useThreads';
import {EmployeeMentionTypeaheadOption} from 'components/DSYS/editor/plugins/MentionPlugin';
import {CollabUser} from 'components/DSYS/editor/utils/collabProvider';
import {Layout} from 'components/DSYS/Layout';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {isDev, isProd} from 'helpers/environment';
import {canManagePlanningTemplates, extractIdFromSlug, isSuperUser} from 'helpers/utils';
import {useCommentActions} from 'hooks/useCommentActions';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useMemoryProfiling} from 'hooks/useMemoryProfiling';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {useYjsDocument} from 'hooks/useYjsDocument';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {$getRoot, EditorState, type LexicalEditor} from 'lexical';
import {NotFound} from 'pages/NotFound';
import {EnhancedHeadingNode} from 'plugins/enhancedHeadings/EnhancedHeadingNode';
import {EnhancedTableCellNode} from 'plugins/enhancedTableCells/EnhancedTableCellNode';
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router';

import {CommentsPanel} from './CommentsPanel';
import {
  useEditorWorkstreams,
  usePlanning,
  usePlanningDocService,
  useRecentPlanningDocs,
} from './hooks';
import styles from './Planning.module.css';
import {PlanningCommentsDrawer, PlanningCommentsDrawerHeader} from './PlanningCommentsDrawer';
import {PlanningDocNav} from './PlanningDocNav';
import {
  PlanningVersionsDrawer,
  PlanningVersionsDrawerHeader,
  useVersionPreviewChildSheet,
} from './PlanningVersionsDrawer';
import {usePlanningCommentScroll} from './usePlanningCommentScroll';
import {usePlanningUrlThreadActivation} from './usePlanningUrlThreadActivation';
import {
  createPlanningDocUrl,
  getCommentsPanelThreads,
  isHardcodedTemplate,
  useFilteredThreads,
} from './utils';

export type PlanningDrawerType = 'comments' | 'versions' | null;

const DocumentWordCount = ({
  editorState,
  maxWords,
}: {
  editorState: EditorState | undefined;
  maxWords: number;
}) => {
  const wordCount = useMemo(() => {
    if (!editorState) return 0;

    return editorState.read(() => {
      const root = $getRoot();
      const allNodes = root.getAllTextNodes();
      let text = '';

      // Filter out text nodes that are inside excluded parent nodes
      allNodes.forEach((textNode) => {
        let isExcluded = false;

        // Check the parent chain for nodes to exclude
        let currentNode = textNode.getParent();
        while (currentNode) {
          if (
            currentNode instanceof EnhancedHeadingNode ||
            currentNode instanceof EnhancedTableCellNode ||
            currentNode instanceof PlaceholderNode ||
            currentNode instanceof ExcludeWordcountNode
          ) {
            isExcluded = true;
            break;
          }
          currentNode = currentNode.getParent();
        }

        if (!isExcluded) {
          text += textNode.getTextContent() + ' ';
        }
      });

      // Count words from the combined text
      return text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
    });
  }, [editorState]);

  const isOverLimit = wordCount > maxWords;

  const getDisplayText = () => {
    return `${wordCount} / ${maxWords} ${t('word_count', {count: maxWords})}`;
  };

  return (
    <Text
      className={cx(
        atoms({
          color: isOverLimit ? 'Alert Base' : 'Text Subtle',
        })
      )}
    >
      {getDisplayText()}
    </Text>
  );
};

export const PlanningDoc = () => {
  const params = useParams() as {id: string};
  const [searchParams] = useSearchParams();
  const planningDocId = extractIdFromSlug(params.id);

  const {planning, isLoading, isError} = usePlanning(planningDocId);
  useMemoryProfiling({documentId: planningDocId});
  const {addToRecentPlanningDocs} = useRecentPlanningDocs();
  const {yjsContent} = useYjsDocument(planning?.document?.id);

  const {threads} = useThreadsWithoutContext(planning?.document?.id);
  const filteredActiveThreads = useFilteredThreads(threads);

  useEditorWorkstreams(planningDocId); // prefetch workstreams

  const drawerRef = useRef<HTMLDivElement>(null);
  const activeComment = useAtomValue(activeCommentAtom);
  const {openComment, closeComment} = useCommentActions();
  const commentsPanelThreads = useMemo(
    () => getCommentsPanelThreads(threads, filteredActiveThreads, activeComment?.threadId),
    [threads, filteredActiveThreads, activeComment?.threadId]
  );
  const focusedThread = useAtomValue(focusedThreadAtom);
  const pulseUser = useAtomValue(pulseUserAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const isPublished =
    planning?.template_type === EditorDocTemplateType.NARRATIVE &&
    planning?.status === EditorDocStatus.COMPLETE;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState<PlanningDrawerType>(null);
  const [isCollabConnected, setIsCollabConnected] = useState(false);
  const [isDocumentSynced, setIsDocumentSynced] = useState(false);
  const canManageTemplates = canManagePlanningTemplates(pulseUser);
  const [isTemplateMode, setIsTemplateMode] = useState(
    () =>
      (isHardcodedTemplate(planningDocId) || params.id.includes('template') || isDev) &&
      canManageTemplates
  );

  useEffect(() => {
    setIsTemplateMode(
      (isHardcodedTemplate(planningDocId) || params.id.includes('template') || isDev) &&
        canManageTemplates
    );
    // Omit `params.id` from deps: when a template manager toggles template mode, `isTemplateMode`
    // updates before `navigate()` applies `-template` to the path; a params-driven reset would
    // briefly force template mode off. We only re-seed from the slug when the doc or user loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- params.id read intentionally only with planningDocId/pulseUser changes
  }, [planningDocId, pulseUser]);
  const [collabUsers, setCollabUsers] = useState<CollabUser[]>([]);
  const [editorState, setEditorState] = useState<EditorState | undefined>();
  const [planningEditor, setPlanningEditor] = useState<LexicalEditor | null>(null);
  const editorFocusManager = useEditorFocusManagerProvider(planningEditor);
  const {employee, reportingLine} = useAtomValue(loggedInEmployeeAtom);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [editorContainerEl, setEditorContainerEl] = useState<HTMLDivElement | null>(null);
  const [commentsPanelReservePx, setCommentsPanelReservePx] = useState(0);
  const planningTableRightReservePx = activeComment?.threadId
    ? COMMENT_DISPLAY_WIDTH_PX
    : commentsPanelReservePx;

  // Title editing state
  const [userTitle, setUserTitle] = useState<string>(''); // What title user is typing
  const [isEditingTitle, setIsEditingTitle] = useState(false); // Whether user is editing the title

  // Track document view in recent planning docs
  useEffect(() => {
    if (planning?.id) {
      addToRecentPlanningDocs(planning.id);
    }
  }, [planning?.id, addToRecentPlanningDocs]);

  // Version preview
  const canViewVersionHistory = isSuperUser(pulseUser) || planning?.owner?.ldap === pulseUser?.ldap;
  const {selectedVersion, childSheet, handleVersionClick, clearVersion} =
    useVersionPreviewChildSheet(planning?.document?.id || '', {
      enabled: canViewVersionHistory,
    });

  useEffect(() => {
    if (searchParams.get('drawer') === 'versions' && canViewVersionHistory) {
      setActiveDrawer('versions');
      setIsDrawerOpen(true);
    }
  }, [searchParams, canViewVersionHistory]);

  const {updatePlanningDoc} = usePlanningDocService();

  const handleTitleChange = useCallback((title: string) => {
    setUserTitle(title);
  }, []);

  // When user focuses on the title input, capture the current planning name from DB
  const handleTitleFocus = useCallback(() => {
    setIsEditingTitle(true);
    setUserTitle(planning?.name ?? '');
  }, [planning?.name]); // Re-run when planning.name changes so we capture changes from DB

  const saveTitle = useCallback(async () => {
    if (!planning?.id) return;

    const trimmed = userTitle.trim();
    if (!trimmed || trimmed === planning.name) return;

    await updatePlanningDoc({
      planningId: planning.id,
      update: {name: trimmed},
    });
  }, [planning?.id, planning?.name, updatePlanningDoc, userTitle]);

  const handleTitleBlur = useCallback(() => {
    void saveTitle().finally(() => {
      setIsEditingTitle(false);
    });
  }, [saveTitle]);
  const titleValue = isEditingTitle ? userTitle : (planning?.name ?? '');

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    clearVersion();
  }, [clearVersion]);

  const handleCommentsPanelSelect = useCallback(
    (threadId: string, originId?: string | null) => {
      openComment({
        threadId,
        source: 'preview',
        planningCommentOriginId: originId,
      });
    },
    [openComment]
  );

  const isEditorStateReady = Boolean(editorState);
  usePlanningUrlThreadActivation({
    urlThreadId: searchParams.get('thread'),
    locationKey: location.key,
    navigationState: location.state,
    planningId: planning?.id,
    planningDocId,
    editorContainerEl,
    planningEditor,
    isDocumentSynced,
    isEditorStateReady,
    openComment,
  });

  usePlanningCommentScroll({
    activeComment,
    editorContainerEl,
    planningEditor,
    planningDocumentId: planning?.document?.id,
    isDocumentSynced,
    isEditorStateReady,
  });

  const handleEditorReady = useCallback((editor: LexicalEditor) => {
    setPlanningEditor((currentEditor) => (currentEditor === editor ? currentEditor : editor));
  }, []);

  // Disable outside click when child sheet (version preview) is open to prevent
  // clicks in the child sheet from closing the main drawer
  useOutsideClick(
    drawerRef,
    selectedVersion ? () => {} : handleCloseDrawer,
    [],
    false,
    'mousedown',
    ['[data-highlight-id]']
  );

  useLayoutEffect(() => {
    const textarea = titleTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [titleValue]);

  // Track if we've scrolled to a hash to prevent duplicate scrolls
  const hasScrolledToHashRef = useRef<string | null>(null);

  useEffect(() => {
    // When the planning doc changes, reset collab connection and editing state
    setIsCollabConnected(false);
    setIsDocumentSynced(false);
    setEditorState(undefined);
    setIsEditingTitle(false);
    hasScrolledToHashRef.current = null;
  }, [planning?.id]);

  // Scroll to heading when deep link hash is present
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const hashMatch = hash.match(/^#hid=(.+)$/);
    if (!hashMatch) return;

    const shortId = decodeURIComponent(hashMatch[1]);
    const headingId = shortId.startsWith('heading-') ? shortId : `heading-${shortId}`;

    // Prevent duplicate scrolls
    if (hasScrolledToHashRef.current === headingId) return;

    const scrollToElement = (element: Element) => {
      hasScrolledToHashRef.current = headingId;
      const top = window.scrollY + element.getBoundingClientRect().top - 80;
      window.scrollTo({top, behavior: 'smooth'});
    };

    // Check if element already exists
    const existingElement = document.querySelector(`[data-heading-id="${headingId}"]`);
    if (existingElement) {
      scrollToElement(existingElement);
      return;
    }

    // Use MutationObserver to watch for the element to appear
    const observer = new MutationObserver(() => {
      const element = document.querySelector(`[data-heading-id="${headingId}"]`);
      if (element) {
        observer.disconnect();
        // Small delay to ensure layout is complete
        requestAnimationFrame(() => {
          scrollToElement(element);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-heading-id'],
    });

    // Timeout fallback - stop observing after 15 seconds
    const timeoutId = setTimeout(() => {
      observer.disconnect();
    }, 15000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []); // Run once on mount

  useEffect(() => {
    if (planning) {
      const useTemplateInUrl = canManageTemplates ? isTemplateMode : params.id.includes('template');
      const updatedUrl = createPlanningDocUrl(planning, useTemplateInUrl);
      const currentUrl = `${location.pathname}${location.search}`;

      if (updatedUrl !== currentUrl) {
        navigate(updatedUrl, {replace: true, preventScrollReset: true});
      }
    }
  }, [
    canManageTemplates,
    isTemplateMode,
    location.pathname,
    location.search,
    navigate,
    params.id,
    planning,
  ]);

  useEffect(() => {
    if (activeDrawer !== 'versions') {
      clearVersion();
    }
  }, [activeDrawer, clearVersion]);

  useDocumentTitle(planning?.name || t('untitled'));

  const commentMentions = useMemo(() => {
    const docEmployees = [
      planning?.owner,
      planning?.document?.owner,
      ...(planning?.employees ?? []),
    ].filter((emp): emp is EmployeeRoleLevel => Boolean(emp?.user_id));

    const uniqueDocEmployees = Array.from(
      new Map(docEmployees.map((emp) => [emp.user_id, emp])).values()
    ).filter((emp) => emp.user_id !== employee.user_id);

    const mentionEmployees = uniqueDocEmployees.length
      ? uniqueDocEmployees
      : Array.from(
          new Map(reportingLine.slice(1).map((emp) => [emp.user_id, emp])).values()
        ).filter((emp) => emp.user_id !== employee.user_id);

    return mentionEmployees.map((emp) => new EmployeeMentionTypeaheadOption(emp));
  }, [
    planning?.owner,
    planning?.document?.owner,
    planning?.employees,
    reportingLine,
    employee.user_id,
  ]);

  const handleDrawerToggle = useCallback(
    (drawerType: PlanningDrawerType) => {
      setActiveDrawer((current) => {
        if (!closeComment()) {
          return current;
        }
        if (!drawerType) {
          setIsDrawerOpen(false);
          return null;
        } else if (current === drawerType && isDrawerOpen) {
          // Same drawer clicked - close it
          setIsDrawerOpen(false);
          return null;
        } else {
          // Different drawer or no drawer open - open/switch to new drawer
          setIsDrawerOpen(true);
          return drawerType;
        }
      });
    },
    [isDrawerOpen, closeComment]
  );

  const showWordCount = planning?.template_type === EditorDocTemplateType.NARRATIVE;

  if (isError && !planning) {
    return <NotFound />;
  }

  if (!pulseUser) {
    return null;
  }

  if (!planning?.document?.id || isLoading) {
    return null;
  }

  return (
    <>
      <PlanningDocNav
        activeDrawer={isDrawerOpen ? activeDrawer : null}
        onDrawerToggle={handleDrawerToggle}
        showTemplateModeToggle={canManageTemplates && isCollabConnected}
        isTemplateMode={isTemplateMode}
        onTemplateModeChange={setIsTemplateMode}
      />
      <Layout.InlineDrawerContainer
        containerRef={drawerRef}
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        width="100%"
        pushContent={false} // this is still a wip
        drawerWidth={selectedVersion ? '813px' : '420px'}
        childSheet={childSheet}
        drawerHeader={
          activeDrawer === 'comments' ? (
            <PlanningCommentsDrawerHeader documentId={planning.document.id} />
          ) : activeDrawer === 'versions' ? (
            <PlanningVersionsDrawerHeader documentId={planning.document.id} />
          ) : null
        }
        drawerIcon={
          activeDrawer === 'comments'
            ? CommentLine
            : activeDrawer === 'versions'
              ? ClockLine
              : undefined
        }
        drawerBody={
          activeDrawer === 'comments' ? (
            <PlanningCommentsDrawer
              documentId={planning.document.id}
              editorContainerEl={editorContainerEl}
              handleCloseDrawer={handleCloseDrawer}
            />
          ) : activeDrawer === 'versions' ? (
            <PlanningVersionsDrawer
              documentId={planning.document.id}
              onVersionClick={handleVersionClick}
            />
          ) : null
        }
      >
        <div
          ref={setEditorContainerEl}
          className={styles.editorContainer}
          style={{
            maxWidth: 765,
            width: '100%',
            marginTop: 0,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            paddingLeft="8"
            paddingRight="10"
            marginBottom="32"
          >
            <textarea
              key={`planning-doc-title-${planning?.id}`}
              ref={titleTextareaRef}
              name="title"
              className={styles.titleInput}
              placeholder={isCollabConnected && !isLoading ? t('untitled') : undefined}
              value={titleValue}
              rows={1}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleTitleChange(e.target.value)
              }
              onFocus={handleTitleFocus}
              onBlur={handleTitleBlur}
            />
            {showWordCount && (
              <Box flexShrink={0}>
                <DocumentWordCount editorState={editorState} maxWords={1300} />
              </Box>
            )}
          </Box>

          <EditorFocusManagerContext.Provider value={editorFocusManager}>
            <DocumentThreadsProvider documentId={planning.document.id}>
              <RichTextArea
                key={`planning-doc-editor-${planning.id}`}
                editable={!isPublished}
                disableTableSelection={isPublished}
                mentions
                paddingX="10"
                enhancedHeadings={isCollabConnected ? 'enabled' : undefined}
                enhancedTableCells
                planningId={planning.id}
                collabId={planning.document.id}
                collabOwner={planning.document.owner}
                autoFocus={false}
                hideToolbar
                selectionControls={
                  isPublished
                    ? ['comments']
                    : [
                        'headers',
                        'templates',
                        'styling',
                        'enhancements',
                        'insertions',
                        'table',
                        'comments',
                      ]
                }
                alwaysShowSelectionControls
                toc={isCollabConnected}
                commentMentions={commentMentions}
                activeThreadId={focusedThread}
                disableBorder
                table
                media
                value={planning.document.content ?? undefined}
                yjsValue={yjsContent ?? undefined}
                profile="paper"
                source="planning-doc"
                disableAISuggestions
                placeholder={t('write_something')}
                planningTableRightReservePx={planningTableRightReservePx}
                onChange={(state) => {
                  setEditorState(state);
                }}
                onEditorReady={handleEditorReady}
                onCollabConnected={setIsCollabConnected}
                onDocumentSynced={setIsDocumentSynced}
                onCollabUsersChange={setCollabUsers}
                editorRole={isTemplateMode ? 'template' : 'document'}
                onSectionChanged={(headingKey, text, settings) => {
                  console.log('Enhanced Headings - Section changed:', {
                    headingKey,
                    text: text.substring(0, 100) + (text.length > 100 ? '…' : ''),
                    settings,
                    timestamp: new Date().toISOString(),
                  });
                }}
              />
            </DocumentThreadsProvider>
            <CommentsPanel
              containerEl={editorContainerEl}
              threads={commentsPanelThreads}
              activeThreadId={activeComment?.threadId}
              activeComment={activeComment}
              onThreadSelect={handleCommentsPanelSelect}
              onRightReserveChange={setCommentsPanelReservePx}
            />
          </EditorFocusManagerContext.Provider>

          {/* Debug info */}
          {!isProd && (
            <Text size="xsmall" color="subtle">
              {t('planning_debug_document_id', {
                documentId: planning?.document?.id ?? '\u2014',
              })}{' '}
              <span style={{color: isCollabConnected ? 'green' : 'orange'}}>
                {t(isCollabConnected ? 'yes' : 'no')}
              </span>
              {t('planning_debug_users', {count: collabUsers.length})}
            </Text>
          )}
        </div>
      </Layout.InlineDrawerContainer>
    </>
  );
};
